import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  createOrderDocuments,
  getOrderDocumentsById,
  updateOrderDocuments,
} from "services/order.services";
import { getPorts, getTotalSubSuppliers } from "services/sub_suppliers.services";
import {
  setOrderDocumnetsDetails,
  setSelectedSubTierId,
  toggleIsForwarderCreateModalOpen,
  toggleIsOrderDocumentsEditable,
  toggleIsSubSupplierCreateModalOpen,
  toggleOrderDocumentsShowDiscardModal,
  toggleIsSelectedSupplier,
} from "store/orderSlice";
import { formateLable, formattedDateIntoObject } from "utils/helper";
import { createForwarderSchema } from "validation/order.validation";
import useCustomToaster from "./useCustomToaster";
const useCreateUpdateForwarder = ({ refetchOrderDetails }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId, orderId } = useParams();
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileData, setFileData] = useState([]);
  const [showFileData, setShowFileData] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [hasImage, setHasImage] = useState(false);
  const [subSupplierList, setSubSupplierList] = useState([]);
  const [portList, setPortList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { showCustomToast } = useCustomToaster();
  const [isInitialValuesSet, setIsInitialValuesSet] = useState(false);
  const [isManuallyValuesSet, setIsManuallyValuesSet] = useState(false);
  const [forwarderId, setForwarderId] = useState(null);
  const {
    selectedSubTierId,
    selectedOrderDocumentsId,
    isOrderDocumentsEditable,
    orderDocumnetsDetails,
    isForwarderCreateModalOpen,
    isSubSupplierCreateModalOpen,
    isSelectedSupplier,
    selectedTierType,
  } = useSelector((state) => state.orderSlice);

  const {
    control,
    register,
    reset,
    setValue,
    getValues,
    handleSubmit,
    setError,
    clearErrors,
    setFocus,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues: {
      workspace_id: parseInt(workspaceId),
      order_id: parseInt(orderId),
      sub_supplier_id: null,
      ship_to: null,
      ship_from: null,
      sub_tier_id: null,
      forwarder: "yes",
      arrive_date: { startDate: "", endDate: "" },
      dispatch_date: { startDate: "", endDate: "" },
    },
    resolver: zodResolver(createForwarderSchema),
  });

  const {
    data: queryData,
    isLoading: isArticleTypeListLoading,
    refetch: refetchSubSupplierData,
  } = useQuery({
    queryKey: ["totalSubSuppliersData", "getTotalSubSuppliersData", workspaceId],
    queryFn: () => getTotalSubSuppliers({ workspace_id: parseInt(workspaceId), pagination: "no" }),
    enabled: !!workspaceId,
  });

  const {
    data: portQueryData,
    isLoading: isPortQueryDatLoading,
    refetch: refetchPortQueryData,
  } = useQuery({
    queryKey: ["ports", "getPorts", workspaceId],
    queryFn: () => getPorts({ workspace_id: parseInt(workspaceId) }),
    enabled: !!workspaceId,
  });

  const orderDocumentsQuery = useQuery({
    queryKey: ["orderDocuments", "get", selectedOrderDocumentsId],
    queryFn: () => getOrderDocumentsById(selectedOrderDocumentsId),
    enabled: !!selectedOrderDocumentsId,
  });

  const setDefaultFormValue = async (details) => {
    if (!details && !selectedOrderDocumentsId) return;
    if (details) {
      const {
        sub_supplier,
        sub_supplier_id,
        supplier,
        supplier_id,
        sub_tier,
        sub_tier_id,
        arrive_date,
        dispatch_date,
        files,
        order_document_files,
        workspace_id,
        forwarder,
      } = details;

      setValue("arrive_date", formattedDateIntoObject(forwarder.arrive_date));
      setValue("dispatch_date", formattedDateIntoObject(forwarder.dispatch_date));
      if (forwarder) {
        setForwarderId(forwarder?.id);
        setValue("forwarder", "yes");
      }
      if (forwarder && forwarder.ship_from) {
        setValue("ship_from", {
          label: formateLable(forwarder.ship_from.name, forwarder.ship_from.code),
          customLabel: `${forwarder.ship_from.name}${" "}${forwarder.ship_from.code}`,
          value: forwarder.ship_from?.id,
        });
      }
      if (forwarder && forwarder.ship_to) {
        setValue("ship_to", {
          label: formateLable(forwarder.ship_to.name, forwarder.ship_to.code),
          customLabel: `${forwarder.ship_to.name}${" "}${forwarder.ship_to.code}`,
          value: forwarder?.ship_to?.id,
        });
      }
      if (sub_supplier || sub_supplier_id) {
        if (isSelectedSupplier) {
          dispatch(toggleIsSelectedSupplier());
        }
        const matchedType =
          sub_supplier.sub_tiers?.find((t) => t.name === selectedTierType)?.name || "";
        setValue("sub_supplier_id", {
          label: formateLable(sub_supplier.name, matchedType),
          customLabel: `${sub_supplier.name}${" "}${matchedType}`,
          value: sub_supplier?.id,
        });
      }
      if (supplier || supplier_id) {
        if (!isSelectedSupplier) {
          dispatch(toggleIsSelectedSupplier());
        }
        setValue("sub_supplier_id", {
          value: supplier.id,
          label: supplier.name,
          customLabel: `${supplier.name}`,
        });
      }

      if (sub_tier || sub_tier_id) {
        setValue("sub_tier_id", sub_tier?.id);
      }
      setValue("workspace_id", parseInt(workspace_id));
      if (files && files.length > 0) {
        setImagePreviews(files);
        setValue("files", files);
        setFileData(files);
        if (order_document_files && order_document_files.length > 0) {
          const transformedFiles = order_document_files?.map((file) => ({
            url: file.url,
            name: file.file_name,
            size: file.file_size * 1024,
          }));
          setShowFileData(transformedFiles);
          setHasImage(true);
        }
      } else {
        setImagePreviews([]);
        setFileData([]);
        setShowFileData([]);
        setHasImage(false);
      }
      setIsInitialValuesSet(true);
    }
  };

  const createForwarderMutation = useMutation({
    mutationKey: ["forwarder-sub-supplier", "createforwarder"],
    mutationFn: (data) => createOrderDocuments(data),
    onMutate: () => {
      showCustomToast({ title: "Assigning..." }, "createForwarderLoadingToast");
    },
    onSuccess: (res) => {
      toast.dismiss("createForwarderLoadingToast");
      reset();
      clearErrors();
      refetchOrderDetails();
      setFileData([]);
      setImagePreviews([]);
      setShowFileData([]);
      setHasImage(false);
      if (isSubSupplierCreateModalOpen) {
        dispatch(toggleIsSubSupplierCreateModalOpen());
      }
      if (isForwarderCreateModalOpen) {
        dispatch(toggleIsForwarderCreateModalOpen());
      }
      if (isOrderDocumentsEditable) {
        dispatch(toggleIsOrderDocumentsEditable());
      }
      if (selectedSubTierId) {
        dispatch(setSelectedSubTierId());
      }
      if (isSelectedSupplier) {
        dispatch(toggleIsSelectedSupplier());
      }
      showCustomToast({ title: "Assigned successfully" });
      queryClient.invalidateQueries({ queryKey: ["forwarder-sub-supplier", "createforwarder"] });
    },
    onError: (error) => {
      toast.dismiss("createForwarderLoadingToast");
      showCustomToast({
        title: error.response?.data?.message || error.message || "Something went wrong",
      });
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          setError(field, { type: "server", message: messages[0] });
          setFocus(field);
        });
      }
    },
    onSettled: () => {
      toast.dismiss("createForwarderLoadingToast");
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["orderDocuments", "updateOrderDocuments"],
    mutationFn: (data) => updateOrderDocuments(data),
    onMutate: () => {
      showCustomToast({ title: "Updating..." }, "updateOrderDocumentsLoadingToast");
    },
    onSuccess: (res) => {
      toast.dismiss("updateOrderDocumentsLoadingToast");
      reset();
      clearErrors();
      refetchOrderDetails();
      setFileData([]);
      setImagePreviews([]);
      setShowFileData([]);
      setHasImage(false);
      if (isSubSupplierCreateModalOpen) {
        dispatch(toggleIsSubSupplierCreateModalOpen());
      }
      if (isForwarderCreateModalOpen) {
        dispatch(toggleIsForwarderCreateModalOpen());
      }
      if (isOrderDocumentsEditable) {
        dispatch(toggleIsOrderDocumentsEditable());
      }
      if (selectedSubTierId) {
        dispatch(setSelectedSubTierId());
      }
      if (isSelectedSupplier) {
        dispatch(toggleIsSelectedSupplier());
      }
      showCustomToast({ title: "Updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["orderDocuments", "updateOrderDocuments"] });
    },
    onError: (error) => {
      toast.dismiss("updateOrderDocumentsLoadingToast");
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          setError(field, { type: "server", message: messages[0] });
        });
      } else {
        showCustomToast({
          title: error.response?.data?.message || error.message || "Something went wrong",
        });
      }
    },
    onSettled: () => {
      toast.dismiss("updateOrderDocumentsLoadingToast");
      queryClient.invalidateQueries({ queryKey: ["certificate"] });
    },
  });

  const handleConfirmDiscard = (confirm) => {
    dispatch(toggleIsOrderDocumentsEditable());

    if (!confirm) {
      dispatch(toggleOrderDocumentsShowDiscardModal());
      return;
    }

    dispatch(toggleIsOrderDocumentsEditable());
    setDefaultFormValue(orderDocumnetsDetails);
    dispatch(toggleOrderDocumentsShowDiscardModal());
  };

  const toggleEditOrDiscard = () => {
    dispatch(toggleIsOrderDocumentsEditable());
    if (isOrderDocumentsEditable) {
      dispatch(toggleOrderDocumentsShowDiscardModal());
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    onFileDropOrChange(e);
  };

  const isDragEvent = (event) => {
    return "dataTransfer" in event;
  };

  const onFileDropOrChange = (e) => {
    const MAX_FILE_SIZE = 10;
    let files = null;
    if (isDragEvent(e)) {
      files = e.dataTransfer.files;
    } else {
      files = e.target.files;
    }

    if (files && files.length > 0) {
      const newFiles = Array.from(files); // Convert FileList to array
      const validFiles = []; // To store valid files only
      const previewPromises = newFiles.map((file, index) => {
        return new Promise((resolve, reject) => {
          // Check file type
          if (file.type !== "application/pdf" && !file.type.startsWith("image/")) {
            showCustomToast({ title: "Please drop a PDF or image file." });
            reject("Invalid file type");
            return;
          }
          // Check file size
          if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
            showCustomToast({ title: "File size exceeds 10MB limit." });
            reject("File size exceeds limit");
            return;
          }
          // Add valid files to the list
          validFiles.push(file);

          // Generate image preview if it's an image file
          if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.onerror = () => {
              reject("Error reading file");
            };
            reader.readAsDataURL(file);
          } else {
            // Resolve with null for non-image files
            resolve(null);
          }
        });
      });

      Promise.all(previewPromises)
        .then((previews) => {
          setValue("files", [...previews]);
          setImagePreviews((prevPreviews) => [...prevPreviews, ...previews]);
          setFileData((prevFileData) => [...prevFileData, ...validFiles]);
          setShowFileData((prevShowFileData) => [...prevShowFileData, ...validFiles]);
          // Clear the input value to allow re-selection of the same files
          if (e.target && e.target.value) {
            e.target.value = null;
          }
        })
        .catch((error) => {
          console.error("Error processing files:", error);
        });
    }
  };

  const handleCertFileDelete = (index) => {
    // Remove the image preview and corresponding file data
    const updatedPreviews = [...imagePreviews];
    const updatedFileData = [...fileData];
    const updatedShowFileData = [...showFileData];
    updatedPreviews.splice(index, 1);
    updatedFileData.splice(index, 1);
    updatedShowFileData.splice(index, 1);
    setImagePreviews(updatedPreviews);
    setFileData(updatedFileData);
    setValue("files", updatedFileData);
    setShowFileData(updatedShowFileData);
    if (selectedOrderDocumentsId) {
      setIsManuallyValuesSet(true);
      setIsInitialValuesSet(false);
    }
  };
  const onSubmit = (data) => {
    if (getValues("arrive_date") && getValues("dispatch_date")) {
      if (
        new Date(getValues("arrive_date").startDate) <
        new Date(getValues("dispatch_date").startDate)
      ) {
        setError("arrive_date.startDate", {
          type: "custom",
          message: "Check for the ETA date",
        });
        return;
      } else {
        const updatedData = {
          ...data,
          sub_supplier_id: isSelectedSupplier ? null : data.sub_supplier_id,
          supplier_id: isSelectedSupplier ? data.sub_supplier_id : null,
        };
        const processedData = processData(updatedData);
        if (selectedOrderDocumentsId) {
          updateMutation.mutate({ body: processedData, id: selectedOrderDocumentsId });
        } else {
          createForwarderMutation.mutate(processedData);
        }
      }
    }
  };

  const processData = (data) => {
    const processedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        if (typeof value === "object" && value !== null && "label" in value && "value" in value) {
          return [key, value.value];
        }
        if (
          typeof value === "object" &&
          value !== null &&
          "startDate" in value &&
          "endDate" in value
        ) {
          return [key, value.startDate];
        }
        return [key, value];
      }),
    );
    const formData = new FormData();
    Object.entries(processedData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    if (selectedOrderDocumentsId) {
      if (hasImage && fileData && fileData.length === 0) {
        formData.append(`files[]`, []);
      } else if (fileData && fileData.length > 0) {
        fileData.forEach((file, index) => {
          formData.append(`files[${index}]`, file); // Append each file individually with a dynamic key
        });
      }
    } else if (fileData && fileData.length > 0) {
      fileData.forEach((file, index) => {
        formData.append(`files[${index}]`, file); // Append each file individually with a dynamic key
      });
    }
    return formData;
  };

  const onValidationError = (error) => {
    console.log(error);
  };

  useEffect(() => {
    const articleTypes = queryData;
    if (articleTypes && articleTypes.data && articleTypes.data.sub_supplier) {
      const updatedArticleTypeList = articleTypes?.data?.sub_supplier?.map((item) => {
        const matchedType = item.sub_tiers?.find((t) => t.type === selectedTierType)?.type || "";
        return {
          ...item,
          value: item.id,
          label: formateLable(item.name, matchedType),
          customLabel: `${item.name} ${matchedType}`,
        };
      });
      setSubSupplierList(updatedArticleTypeList);
    }
  }, [queryData]);

  useEffect(() => {
    if (portQueryData && portQueryData?.data?.ports && portQueryData?.data?.ports?.length > 0) {
      const updatedPortList = portQueryData?.data?.ports?.map((item) => ({
        ...item,
        value: item.id,
        label: formateLable(item.name, item.code),
        customLabel: `${item.name}${" "}${item.code}`,
      }));
      setPortList(updatedPortList);
    }
  }, [portQueryData]);

  useEffect(() => {
    if (
      selectedOrderDocumentsId &&
      orderDocumentsQuery &&
      orderDocumentsQuery.isSuccess &&
      !orderDocumentsQuery.isError
    ) {
      const result = orderDocumentsQuery?.data?.data?.data;
      if (result) {
        dispatch(setOrderDocumnetsDetails(result));
        setDefaultFormValue(result);
      }
    }
  }, [orderDocumentsQuery?.data, selectedOrderDocumentsId]);

  return {
    control,
    subSupplierList,
    portList,
    errors,
    isLoading: isArticleTypeListLoading,
    isPending: createForwarderMutation.isPending,
    register,
    reset,
    handleSubmit,
    onSubmit,
    onValidationError,
    getValues,
    isDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    onFileDropOrChange,
    imagePreviews,
    setValue,
    toggleEditOrDiscard,
    handleConfirmDiscard,
    clearErrors,
    setError,
    fileData,
    handleCertFileDelete,
    showFileData,
    selectedCountry,
    setSelectedCountry,
    refetchSubSupplierData,
    isInitialValuesSet,
    isManuallyValuesSet,
    setIsManuallyValuesSet,
    setIsInitialValuesSet,
    forwarderId,
    setForwarderId,
  };
};

export default useCreateUpdateForwarder;
